import { appSession } from '../../lib/safeStorage';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Briefcase, Flame, Megaphone } from 'lucide-react';
import { SHOW_EXPERIMENTAL_FEATURES } from '../../lib/featureFlags';

// Mobile-only bottom tab bar for staff-role users. Renders nothing for other
// roles and is hidden on lg+ (where the desktop sidebar takes over).
// Order left→right: Home · News (Announcements) · Gigs.
const TABS = [
  { key: 'home', label: 'Home', icon: Home, path: '/staff-portal', match: ['/staff-portal'] },
  { key: 'news', label: 'News', icon: Megaphone, path: '/staff/announcements', match: ['/staff/announcements'] },
  { key: 'gigs', label: 'Gigs', icon: Briefcase, path: '/staff/my-assignments', match: ['/staff/my-assignments'] },
  { key: 'heat', label: 'Heat', icon: Flame, path: '/staff/leaderboard', match: ['/staff/leaderboard'] },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function StaffBottomNav({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const enabled = !!user && user.role === 'staff';
  // Heat (leaderboard) tab is an experimental surface — hidden unless the flag is on.
  const tabs = SHOW_EXPERIMENTAL_FEATURES ? TABS : TABS.filter((t) => t.key !== 'heat');

  const isActive = (tab) =>
    tab.match.some((m) => location.pathname === m || location.pathname.startsWith(m + '/'));

  // Finger-tracking horizontal swipe between the bottom-nav tabs. The current
  // page (.staff-page-enter wrapper) follows the finger in real time and
  // settles with a spring; past a threshold / on a quick flick it navigates to
  // the neighbouring tab, otherwise it snaps back. Gestures that start inside a
  // horizontally-scrollable container OR any subtree marked `data-swipe-ignore`
  // are ignored so carousels, tables and the brief drawer keep their own swipe.
  useEffect(() => {
    if (!enabled) return undefined;
    const reduced = prefersReducedMotion();
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let startTarget = null;
    let decided = false;   // has this gesture been classified yet
    let dragging = false;  // classified as a horizontal tab-swipe
    let pageEl = null;
    let lockUntil = 0;

    const inScrollableAncestor = (node) => {
      let cur = node;
      while (cur && cur !== document.body) {
        if (cur.nodeType === 1 && cur.scrollWidth > cur.clientWidth + 5) return true;
        cur = cur.parentNode;
      }
      return false;
    };

    const clearPageTransform = (animate) => {
      if (!pageEl) return;
      const el = pageEl;
      if (animate && !reduced) {
        el.style.transition = 'transform 300ms cubic-bezier(.22,1,.36,1)';
        el.style.transform = 'translateX(0px)';
        window.setTimeout(() => { el.style.transition = ''; el.style.transform = ''; }, 320);
      } else {
        el.style.transition = '';
        el.style.transform = '';
      }
    };

    const reset = () => {
      startTarget = null;
      decided = false;
      dragging = false;
      pageEl = null;
    };

    const onStart = (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      startX = t.clientX;
      startY = t.clientY;
      startTime = Date.now();
      startTarget = e.target;
      decided = false;
      dragging = false;
      pageEl = document.querySelector('.staff-page-enter');
    };

    const onMove = (e) => {
      if (!startTarget) return;
      const t = e.touches && e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!decided) {
        if (absX < 8 && absY < 8) return; // too small to classify yet
        // Vertical scroll, or an ignored/scrollable subtree → not our gesture.
        if (
          absY > absX ||
          (startTarget.closest && startTarget.closest('[data-swipe-ignore]')) ||
          inScrollableAncestor(startTarget)
        ) {
          decided = true;
          dragging = false;
          return;
        }
        decided = true;
        dragging = true;
      }

      if (!dragging || reduced || !pageEl) return;

      // Rubber-band resistance at the two ends so you can't drag past the edge.
      const idx = tabs.findIndex(isActive);
      const atStart = idx <= 0 && dx > 0;
      const atEnd = idx >= tabs.length - 1 && dx < 0;
      const applied = atStart || atEnd ? dx * 0.32 : dx;
      pageEl.style.transition = 'none';
      pageEl.style.transform = `translateX(${applied}px)`;
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = (e) => {
      if (!startTarget) { reset(); return; }
      if (!dragging) { reset(); return; }
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) { clearPageTransform(true); reset(); return; }
      const dx = t.clientX - startX;
      const absX = Math.abs(dx);
      const elapsed = Date.now() - startTime;
      const flick = elapsed < 260 && absX > 45;

      const idx = tabs.findIndex(isActive);
      let targetIdx = null;
      let dir = null;
      if (dx < 0 && idx > -1 && idx < tabs.length - 1) { targetIdx = idx + 1; dir = 'left'; }
      else if (dx > 0 && idx > 0) { targetIdx = idx - 1; dir = 'right'; }

      const now = Date.now();
      const shouldNav = targetIdx !== null && (absX > 70 || flick) && now >= lockUntil;

      if (shouldNav) {
        lockUntil = now + 450;
        try { appSession.setItem('staffNavDir', dir); } catch (_) { /* appSession disabled */ }
        // The .staff-page-enter wrapper is keyed by pathname, so it remounts on
        // navigate and its inline transform is discarded — the incoming page
        // plays its own direction-aware entry animation.
        navigate(tabs[targetIdx].path);
      } else {
        clearPageTransform(true);
      }
      reset();
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const activeIndex = tabs.findIndex(isActive);

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-[60] border-t border-white/10 bg-[#0B0F1A]/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
      data-testid="staff-bottom-nav"
      aria-label="Staff navigation"
    >
      {/* iOS-style page dots — a visible hint that the tabs are swipeable. */}
      <div className="flex items-center justify-center gap-1.5 pt-2" data-testid="staff-nav-dots" aria-hidden="true">
        {tabs.map((tab, i) => (
          <span
            key={tab.key}
            className={`rounded-full transition-all duration-300 ${
              i === activeIndex ? 'w-4 h-1.5 bg-[#D4AF37]' : 'w-1.5 h-1.5 bg-white/25'
            }`}
          />
        ))}
      </div>

      {/* flex + flex-1 per tab: self-balancing at ANY tab count. */}
      <div className="flex">
        {tabs.map((tab, tabIdx) => {
          const active = isActive(tab);
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => {
                // Record direction for the incoming page's entry animation so
                // tab TAPS get the same smooth slide-in as swipes.
                const curIdx = tabs.findIndex((t2) => isActive(t2));
                if (curIdx !== -1 && curIdx !== tabIdx) {
                  try {
                    appSession.setItem('staffNavDir', tabIdx > curIdx ? 'left' : 'right');
                  } catch (_) { /* appSession disabled */ }
                }
                navigate(tab.path);
              }}
              data-testid={`staff-nav-${tab.key}`}
              aria-current={active ? 'page' : undefined}
              className={`flex-1 flex flex-col items-center justify-center gap-1.5 py-4 transition-colors ${active ? 'text-[#D4AF37]' : 'text-white/45 hover:text-white/70'}`}
            >
              <Icon className={`w-7 h-7 ${active ? 'drop-shadow-[0_0_6px_rgba(212,175,106,0.5)]' : ''}`} />
              <span className="text-[12px] font-medium tracking-tight">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
